// @flow

/*::

declare var __LUX_VERSION__: string;


type AdditionalTrackingObject = {
  trackingId: string,
  gaOptions: Object
}

type GAObject = {
  excludeComponents: ?Array<string>,
  additionalTracking: AdditionalTrackingObject
}

type AvailableComponentsObject = {
  [string]: Object
}

type OptionsObject = {
  availableComponents: AvailableComponentsObject,
  appName: string,
  appVersion: string,
  ga: GAObject
}

*/

import React from 'react';
import ReactDOM from 'react-dom';

import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { createStore } from 'redux-dynamic-reducer';
import { applyMiddleware, applyToRoot } from 'redux-subspace';
import analytics, { setupLuxEvents } from './redux-analytics';
import ErrorBoundary from './ErrorBoundary';

import ReactGA from 'react-ga';

const componentContainerClass = '.lux-component-container';

let store;
let registeredComponents = [];

/**
 * Remove a class from an element
 *
 * @param {HTMLElement} element HTML element to target
 * @param {string} className the css class to remove
 * @param {HTMLElement} element The targetted element
 */
const removeClass = (element: HTMLElement, className: string): HTMLElement => {
  const regexp = new RegExp('\\b(' + className + ')\\b', 'g');
  element.className = element.className.replace(regexp, '');
  return element;
};

/**
 * Add a class from an element
 *
 * @param {HTMLElement} element HTML element to target
 * @param {string} className the css class to add
 * @param {HTMLElement} element The targetted element
 */
const addClass = (element: HTMLElement, className: string): HTMLElement => {
  const regexp = new RegExp('\\b(' + className + ')\\b', 'g');

  if (!element.className) {
    element.className = className;
    return element;
  }

  if (element.className.search(regexp) === -1) {
    element.className += ' ' + className;
  }

  return element;
};

/**
 * Looks for legacy markup and checks if any components have been migrated across.
 * If legacy markup is found, it replaces it so it suits the new LUX 2.0 format.
 *
 * This does not replace legacy markup if the required component is not available
 * in LUX 2. This allows LUX1 to still continue rendering.
 *
 * @param {object} availableComponents
 * @return {number} number of transformed legacy markups
 */
const transformLegacyMarkup = (availableComponents: Object): number => {
  let returnCount = 0;

  // Get existing legacy containers
  const componentContainers = [
    ...document.querySelectorAll('.component-container')
  ];

  componentContainers.forEach(componentContainer => {
    // For us to query web components we need to get all elements
    // inside the container
    const containerElems = [...componentContainer.getElementsByTagName('*')];

    // Limitation: only one <ux-component/> parent should be in a .component-container
    const luxComponentContainer = containerElems.find(children => {
      return children.tagName.substring(0, 3) === 'UX-';
    });

    // We found a legacy component, name match it with a new component
    // Eg ux-footer-singtel -> FooterSingtel
    if (luxComponentContainer) {
      let newComponentName = luxComponentContainer.tagName.replace('UX-', '');
      newComponentName = toCamelCase(newComponentName, true);

      // If this legacy component is available in new LUX
      // We rewrite the DOM to suit new markup
      if (availableComponents && availableComponents[newComponentName]) {
        let datamodel = luxComponentContainer.getAttribute('datamodel') || '';
        let innerHTML = luxComponentContainer.innerHTML;
        removeClass(componentContainer, 'component-container');
        addClass(componentContainer, 'lux-component-container');
        // LUX 2 still supports the data model prop for backwards support
        componentContainer.setAttribute('datamodel', datamodel);
        componentContainer.setAttribute('component', newComponentName);
        componentContainer.innerHTML = innerHTML;
        returnCount++;
      }
    }
  });

  return returnCount;
};

/**
 * One store to rule them all.
 *
 * We setup one global redux store that is used for all components
 * on the page. Each component is set up like an individual React
 * app that talks to this one store.
 *
 * We namespace each reducer so we do not get naming conflicts
 * if multiple reducers have the same action names.
 *
 * @return {object} store
 */
const setupStore = (): Object => {
  const middlewares = [thunk, applyToRoot(analytics)];

  if (process.env.NODE_ENV === 'development') {
    const { createLogger } = require('redux-logger');

    middlewares.push(
      applyToRoot(
        createLogger({
          collapsed: true,
          duration: true,
          diff: true
        })
      )
    );
  }

  store = createStore(undefined, applyMiddleware(...middlewares));
  return store;
};

/**
 * We create a global lux object that holds anything we need globally
 * This includes versions that we inject using our bundler
 * and anything that third party tools may need
 */
const setupGlobalLuxObject = (appName: string, appVersion: string): Object => {
  window.lux = window.lux || {};

  const v2Object = {
    appName: appName,
    appVersion: appVersion,
    coreVersion: __LUX_VERSION__
  };

  window.lux.v2 = v2Object;

  return v2Object;
};

/**
 * Set up global listeners to initalise lux apps
 * Useful for CMS integrations that dynamically generate LUX HTML markup
 * When it needs to ask LUX to initialise after page load
 */
const setupLuxListeners = (options /*: OptionsObject */) => {
  const eventName = 'initLux';

  // A global function that anyone can use to initalise LUX
  if (window.lux && window.lux.v2 && !window.lux.v2.initLux) {
    window.lux.v2.initLux = callback => {
      var event = new CustomEvent(eventName, { detail: callback });
      document.dispatchEvent(event);
    };
  }

  // Add document level event to listen to future components wanting to be rendered
  document.addEventListener(
    eventName,
    event => {
      processWidgets(options).then(results => {
        if (
          event instanceof CustomEvent &&
          typeof event.detail === 'function'
        ) {
          event.detail(results);
        }
      });
    },
    false
  );
};

/**
 * Returns true if the container is inside a LUX legacy container
 * If its true we do not render and wait till LUX1 to finish
 * before we add our LUX2 awesomeness
 *
 * @param {HTMLElement} container the target
 * @return {boolean} if the target  is inside legacy container
 */
const isComponentInsideLuxLegacy = (container: HTMLElement): boolean => {
  let node = container;
  let elems /*: Array<Element> */ = [];

  while (node && node instanceof Element) {
    elems.unshift(node);
    node = node.parentNode;
  }

  const legacyCount = elems.filter(elem => {
    return elem.tagName && elem.tagName.indexOf('UX-') !== -1;
  });

  return legacyCount.length > 0;
};

/**
 * App name might be have the @lux scope (which is good!)
 * and in some cases we want to strip this out
 *
 * For other apps, we just remove the @ from the name
 */
const stripLuxScope = (name: string): string => {
  if (name.indexOf('@lux/') !== -1) {
    return name.split('@lux/')[1];
  }
  return name.replace('@', '');
};

/**
 * Send to GA for usage reporting on when we render a new component
 */
const sendComponentRenderToGa = (
  componentName: string,
  options /*: OptionsObject */
): Object => {
  // Do not allow excluded component
  if (
    options.ga &&
    options.ga.excludeComponents &&
    options.ga.excludeComponents.indexOf(componentName) !== -1
  ) {
    return {};
  }
  // Do not allow GA usage reporting if the window location
  // containes these keywords
  const disallowedHostKeywords = [
    'localhost',
    'test.aws',
    'author-brt',
    's3-website',
    'test',
    'aws',
    'demo'
  ];

  // Is this domain allowed?
  // Returns true if any of the disallowed keywords is found in the url domain
  const domainDisallowed = disallowedHostKeywords.some(v => {
    return window.location.origin.indexOf(v) !== -1;
  });

  if (domainDisallowed) {
    return {};
  }

  const appName = stripLuxScope(options.appName);
  const eventAction = `${componentName} (${appName} v${options.appVersion})`;
  const eventLabel = window.location.origin + window.location.pathname;

  const gaEvent = {
    category: 'LUX Components',
    action: eventAction,
    label: eventLabel,
    nonInteraction: false
  };

  if (process.env.NODE_ENV !== 'test') {
    ReactGA.event(gaEvent, ['lux']);
  }

  return gaEvent;
};

/**
 * Converts my-component to myComponent
 * If pascalCase is true, capitalises the first character, eg MyComponent
 */
const toCamelCase = (value: string, pascalCase: ?boolean): string => {
  const newValue = value
    .replace(/(\w)(\w*)/g, (g0, g1, g2) => {
      return g1.toUpperCase() + g2.toLowerCase();
    })
    .replace(/-/g, '');

  if (!pascalCase) {
    return newValue.substr(0, 1).toLowerCase() + newValue.substr(1);
  } else {
    return newValue;
  }
};

/**
 * The heavy lifting of WidgetManager
 *
 * It loops through the page markup, finds any LUX containers and renders
 * each component as an individual React app that have the ability to
 * talk to each other.
 */

const processWidget = (
  options /*: OptionsObject */,
  componentContainer: HTMLElement
): Promise<*> => {
  return new Promise((resolve, reject) => {
    const availableComponents = options.availableComponents;
    let Component, componentName;

    if (!availableComponents) {
      return reject('You need to provide a list of LUX2 components');
    }

    // If this component is inside a legacy component, we need to wait
    // for LUX1 to load first for us to render it after
    // We do this so we avoid LUX1 (which is slower than LUX2) to override
    // any LUX2 components
    if (isComponentInsideLuxLegacy(componentContainer)) {
      return resolve(
        'Container found inside LUX1 legacy, we will postpone rendering this.'
      );
    }

    // LUX2 components have a particular markup it needs to follow
    // This has changed from LUX1 because there is no need for custom elements
    // And avoids having to iterate through dom elements looking for our selector

    // ComponentContainer needs a data-component attribute
    //
    // We still support 'component' only until other teams havse updated their codebase
    // We moved to data-component because it's valid HTML attribute name
    componentName =
      componentContainer.getAttribute('component') ||
      componentContainer.getAttribute('data-component');

    if (componentName) {
      Component = availableComponents[componentName];
    } else {
      return reject(
        'Component container does not contain a "data-component" attribute ' +
          'which specifies which component to render in this component.'
      );
    }

    // We found a component to render
    if (Component) {
      // The data model becomes the component props
      const propsFromDataModel = JSON.parse(
        componentContainer.getAttribute('datamodel') || '{}'
      );

      // We also support prop-* attributes too
      const propsFromAttributes = [].slice
        .call(componentContainer.attributes)
        // Filter only attributes that start with prop-*
        .filter(attr => attr.name.indexOf('prop-') === 0)
        .reduce((accum, attr) => {
          let propName = attr.name.split('prop-')[1];
          // camel case the prop name
          let camelCasePropName = toCamelCase(propName);
          let value;

          // check if the value is a JSON object
          try {
            value = JSON.parse(attr.value);
          } catch (e) {
            value = attr.value;
          }

          // Set into new object
          accum[camelCasePropName] = value;
          return accum;
        }, {});

      // Props are in priority of prop-* attributes > datamodel attribute
      const props = Object.assign(propsFromDataModel, propsFromAttributes);

      // each component has a unique count at the end of its name
      let count = 0;

      // If we are rendering a component we have previously registered
      // We will need to namespace it's reducer if it has one so there
      // is no side affects between these components
      // The count is also used for the app shell
      if (registeredComponents.indexOf(componentName + count) !== -1) {
        // Return the first count that has not been used yet.
        count = 1;
        while (registeredComponents.indexOf(componentName + count) !== -1) {
          count++;
        }
      }

      const componentNameWithCount = componentName + count;

      // Create an namespaced instance if this component has a namespace HOC
      // Otherwise it stays as is. Component.createInstance is a property
      // from react-redux-dynamic-reducer, not Redux. It will only exist if
      // this component is wrapped around the dynamic-reducer HOC.
      if ('createInstance' in Component) {
        Component = Component.createInstance(componentNameWithCount);
      }

      ReactDOM.render(
        <Provider store={store}>
          <ErrorBoundary appVersion={options.appVersion}>
            <div id={componentNameWithCount} className={componentName}>
              <Component {...props} />
            </div>
          </ErrorBoundary>
        </Provider>,
        componentContainer,
        () => {
          if (registeredComponents.indexOf(componentNameWithCount) === -1) {
            registeredComponents.push(componentNameWithCount);
          }
          // Send to usage reporting if we are in production
          // (Note This still fires in lower-environments)
          if (process.env.NODE_ENV === 'production') {
            sendComponentRenderToGa(
              componentName || componentNameWithCount,
              options
            );
          }
          // This can be existing to hide component until client side has completed it's rendering
          removeClass(componentContainer, 'hide');
          resolve({
            instance: this,
            component: componentName,
            el: componentContainer,
            props: props
          });
        }
      );
    } else {
      const availableList = Object.keys(availableComponents).join(', ');
      reject(`

        Component not found in LUX: ${componentName}.
        Available components: ${availableList}.
      `);
    }
  });
};

/**
 * Track at this moment a user timing to GA
 *
 * We fire this when we know all components are rendered so we can monitor
 * how long a page takes to load.
 *
 * NOTE: This only works in Singtel Web because of tPast() from legacy LUX.
 * When another app needs this, we will rewrite this function
 */
const trackTiming = (appName: string) => {
  if (
    typeof window.tPast !== 'undefined' &&
    process.env.NODE_ENV === 'production'
  ) {
    ReactGA.timing(
      {
        category: 'LUX Renders',
        variable: stripLuxScope(appName),
        value: Number(window.tPast()) * 1000
      },
      ['lux']
    );
  }
};

/**
 * Loops through all LUX containers on the HTML markup and looks for
 * any LUX2 component to render.
 */
const processWidgets = (
  options /*: OptionsObject */,
  selectors: Array<Document> = [document]
): Promise<*> => {
  const componentContainers = selectors.reduce((accum, rootEl) => {
    accum = accum.concat([...rootEl.querySelectorAll(componentContainerClass)]);
    return accum;
  }, []);

  const promises = componentContainers.map(container => {
    return processWidget(options, container);
  });

  return Promise.all(promises).then(results => {
    // Track how long it took to render these set of components
    trackTiming(options.appName);
    return results;
  });
};

/**
 * Set up GA to use as usage reporting for how LUX is used
 * And in the future, the ability for apps to add their own
 * GA accounts to track their own app usages.
 */
const setupAnalytics = (
  options /* :GAObject */,
  appName: string
) /* :Array<AdditionalTrackingObject> */ => {
  //
  // We set up multiple tracking in GA
  //
  // The first one in the list is the lux core tracking we use to track how lux behaves
  let gaList = [
    {
      // Purposely hardcoding the lux-core tracker ID here.
      trackingId: 'UA-68695870-3',
      debug: process.env.NODE_ENV === 'development',
      titleCase: false,
      gaOptions: {
        name: 'lux'
      },
      excludeComponents: (options && options.excludeComponents) || []
    }
  ];

  // Ability to extend with multiple trackers
  // Applications can extend our options to do their own GA tracking
  if (options && options.additionalTracking) {
    // Create new GA init object
    let customTracker = Object.assign({}, options.additionalTracking);
    customTracker.debug = process.env.NODE_ENV === 'development';
    customTracker.titleCase = false;

    // Format the gaOptions parameter
    let customGaOptions = Object.assign(
      {},
      options.additionalTracking.gaOptions || {}
    );

    // Default value for tracker name is the app name
    if (!customGaOptions.name) {
      customGaOptions.name = appName;
    } else if (customGaOptions.name === 'lux') {
      // make sure no one is using the 'lux' tracker name
      customGaOptions.name = appName;
    }

    customTracker.gaOptions = customGaOptions;
    gaList.push(customTracker);
  }

  if (process.env.NODE_ENV !== 'test') {
    ReactGA.initialize(gaList);
  }

  return gaList;
};

/**
 * Render LUX components found on the page document.
 *
 * It goes through the HTML of the page and looks for a particular
 * markup that tells us where a LUX component belongs on the page.
 * It replaces the markup with a React component.
 *
 * It will only render components passed in the parameters.
 *
 * @param {object} options e
 */
const renderFromMarkup = (options /*: OptionsObject */): Promise<*> => {
  transformLegacyMarkup(options.availableComponents);
  setupLuxEvents();
  setupStore();
  setupGlobalLuxObject(options.appName, options.appVersion);
  setupLuxListeners(options);
  setupAnalytics(options.ga, options.appName);
  return processWidgets(options);
};

const init = (options /*: OptionsObject */): Promise<*> => {
  if (process.env.NODE_ENV === 'development') {
    window['console'].warn(
      '[LUX] widgetManager.init is depreciated. ' +
        'Read more on our documentation in which API you should be using.'
    );
  }
  return renderFromMarkup(options);
};

export default {
  init,
  processWidgets,
  processWidget,
  isComponentInsideLuxLegacy,
  removeClass,
  addClass,
  transformLegacyMarkup,
  setupStore,
  setupGlobalLuxObject,
  setupLuxListeners,
  stripLuxScope,
  sendComponentRenderToGa,
  setupAnalytics,
  renderFromMarkup
};
